import React from 'react';
import { ReactComponent as PersonalIcon } from '../../../../assets/icons/svg/colored-icons/ic-colored-categorization-personal.svg';
import { ReactComponent as SplitIcon } from '../../../../assets/svg/categorization-icons/split-icon.svg';
import { ReactComponent as BusinessIcon } from '../../../../assets/icons/svg/colored-icons/ic-colored-categorization-business.svg';
import { ReactComponent as SettingsIcon } from '../../../../assets/svg/categorization-icons/settings-icon.svg';
import {
  showUserSplitOption,
  canUserCategorize,
  isCategorized,
  calculateBusinessPercentAmount,
  isCategorizationTypeIncome,
  isCategorizedAsBusiness,
  isCategorizedAsPersonal,
} from '../../../transactions/utils/categorization';
import { ITransaction } from '../../../../types';
import { convertValueToUSD } from '../../../../helpers/currency.helper';
import { ACTION_TYPES } from '../../constants';

const STYLES = {
  UNCATEGORIZED: {
    MAIN: 'w-full h-[75px] rounded-md bg-bw02 justify-center items-center flex',
    ICON_WRAPPER: 'w-[50px] h-[50px] flex items-center justify-center rounded-full border-[1px] border-bw03',
    ICON_SHARED: 'cursor-pointer',
  },
  CATEGORIZED: {
    MAIN: 'w-full h-[42px] bg-bw02 border-bw05 rounded-sm border-[1px] pl-[12px] pr-[10px] flex items-center justify-between relative z-1',
    ARROW:
      'absolute top-[-4px] left-[12px] after:content-[""] after:block after:absolute after:border-t-[1px] after:border-r-[1px] after:border-b-[0] after:border-l-[0] after:border-solid after:border-bw05 after:w-[9px] after:h-[9px] after:bg-bw02 after:transform after:rotate-[315deg] after:left-[7px] after:top-[-1px] after:z-[999]',
    TEXT: 'font-metricRMedium text-[12px] text-bw06 leading-3 ml-[6px]',
  },
};

interface ICategorizationStrip {
  transaction: ITransaction;
  handleCategorizationActionClick: (actionType: string) => void;
  isReadOnly?: boolean;
}

export const CategorizationStrip = ({
  transaction,
  handleCategorizationActionClick,
  isReadOnly,
}: ICategorizationStrip) => {
  if (!canUserCategorize(transaction.allowCategorizationEnum, transaction.categorizationType || '')) return null;
  const amountToDisplay = convertValueToUSD(
    calculateBusinessPercentAmount(transaction.amount, transaction.categoryDetails.businessPercent),
    2,
  );
  const renderCategorizedView = () => {
    const businessPercent = transaction?.categoryDetails?.businessPercent;
    if (!businessPercent) return null;
    return (
      <div className={STYLES.CATEGORIZED.MAIN}>
        <div className={STYLES.CATEGORIZED.ARROW} />
        <div className='flex items-center mt-[2px]'>
          <BusinessIcon
            className='w-[24px] h-[24px] relative top-[-2px]'
            data-testid='categorization-split-business-icon'
          />
          <div className={STYLES.CATEGORIZED.TEXT}>
            <span data-testid='categorization-strip-split-percent'>
              {Math.trunc(Math.round(transaction.categoryDetails.businessPercent * 100))}
            </span>
            %
            <span className='px-[4px]'>|</span>
            <span data-testid='categorization-strip-split-amount'>{amountToDisplay}</span>
          </div>
        </div>
        <SettingsIcon
          data-testid='categorization-split-settings-icon'
          className='cursor-pointer'
          onClick={() => handleCategorizationActionClick(ACTION_TYPES.SPLIT)}
        />
      </div>
    );
  };

  const handleSplit = () => {
    if (!isReadOnly) {
      handleCategorizationActionClick(ACTION_TYPES.SPLIT);
    }
  };

  const renderUncategorizedView = () => (
    <div className={STYLES.UNCATEGORIZED.MAIN}>
      <button
        disabled={isReadOnly}
        aria-label='categorize as personal'
        type='button'
        onClick={() => handleCategorizationActionClick(ACTION_TYPES.LIFE)}
        className={`${STYLES.UNCATEGORIZED.ICON_SHARED} ${STYLES.UNCATEGORIZED.ICON_WRAPPER} ${isReadOnly ? 'bg-bw03 cursor-not-allowed' : 'bg-bw00'} mr-[10px]`}
        data-testid='categorization-split-action-personal'
      >
        <PersonalIcon className='w-[28px] h-[28px]' />
      </button>
      {showUserSplitOption(transaction?.allowCategorizationEnum, transaction?.categorizationType || '') && (
        <SplitIcon
          onClick={handleSplit}
          className={`${STYLES.UNCATEGORIZED.ICON_SHARED} w-[35px] h-[35px] ${isReadOnly ? 'stroke-bw03 cursor-not-allowed' : ''}`}
          data-testid='categorization-split-action-split'
        />
      )}
      <button
        disabled={isReadOnly}
        aria-label='categorize as business'
        type='button'
        onClick={() => handleCategorizationActionClick(ACTION_TYPES.WORK)}
        className={`${STYLES.UNCATEGORIZED.ICON_SHARED} ${STYLES.UNCATEGORIZED.ICON_WRAPPER} ${isReadOnly ? 'bg-bw03 cursor-not-allowed' : 'bg-bw00'} ml-[10px]`}
        data-testid='categorization-split-action-business'
      >
        <BusinessIcon className='w-[28px] h-[28px]' />
      </button>
    </div>
  );
  const isIncomeType = isCategorizationTypeIncome(transaction.categorizationType || '');
  const shouldRenderCategorizedView =
    isCategorized(transaction.categoryDetails) &&
    isCategorizedAsBusiness(transaction.categoryDetails) &&
    isCategorizedAsPersonal(transaction.categoryDetails);
  const shouldRenderUncategorizedView = !isCategorized(transaction.categoryDetails);

  return (
    <>
      {!isIncomeType && shouldRenderUncategorizedView && renderUncategorizedView()}
      {!isIncomeType && shouldRenderCategorizedView && renderCategorizedView()}
    </>
  );
};
